import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';

function Layout({ children, title, showNav = true }) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBottomNav, setShowBottomNav] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Auto-hide navigation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        // At top of page, always show
        setShowBottomNav(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up, show nav
        setShowBottomNav(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 80) {
        // Scrolling down, hide nav
        setShowBottomNav(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      )
    },
    {
      path: '/input',
      label: 'Eingabe',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      )
    },
    {
      path: '/entrytable',
      label: 'Einträge',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="9" y1="15" x2="15" y2="15"/>
          <line x1="9" y1="11" x2="15" y2="11"/>
        </svg>
      )
    },
    {
      path: '/customize',
      label: 'Anpassen',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 1v6m0 6v6m8.66-13.66l-5.2 3M8.54 14.34l-5.2 3m18.12 0l-5.2-3M8.54 9.66l-5.2-3"/>
        </svg>
      )
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
      {/* Desktop Sidebar */}
      {showNav && (
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-secondary-800 dark:bg-secondary-950 text-white z-50">
          {/* Logo/Brand */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-secondary-700">
            <h1 className="text-xl font-bold text-white">
              {title || "Frankenfalter"}
            </h1>
            <ThemeToggle />
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                  isActive(item.path)
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'text-secondary-200 hover:bg-secondary-700 hover:text-white'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          {isAuthenticated && (
            <div className="p-4 border-t border-secondary-700 space-y-3">
              <div className="px-2 py-1">
                <p className="text-xs text-secondary-400 mb-1">Angemeldet als</p>
                <p className="text-sm text-white font-medium truncate">{user?.email}</p>
              </div>
              <div className="space-y-2">
                <Link
                  to="/change-password"
                  className="flex items-center w-full px-3 py-2 text-sm text-secondary-200 hover:bg-secondary-700 hover:text-white rounded-lg transition-colors"
                >
                  🔒 Passwort ändern
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-3 py-2 text-sm text-secondary-200 hover:bg-secondary-700 hover:text-white rounded-lg transition-colors text-left"
                >
                  🚪 Abmelden
                </button>
              </div>
            </div>
          )}
        </aside>
      )}

      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-40 bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 shadow-sm">
        <div className="flex items-center justify-between px-4 h-16">
          <h1 className="text-lg font-bold text-secondary-900 dark:text-secondary-50">
            {title || "Frankenfalter"}
          </h1>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {showNav && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && showNav && (
          <div className="border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 shadow-lg">
            <div className="px-4 py-3 space-y-2">
              {isAuthenticated && (
                <>
                  <div className="pb-3 mb-3 border-b border-secondary-200 dark:border-secondary-700">
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Angemeldet als</p>
                    <p className="text-sm text-secondary-900 dark:text-secondary-100 font-medium">{user?.email}</p>
                  </div>
                  <Link
                    to="/change-password"
                    className="flex items-center px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    🔒 Passwort ändern
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors text-left"
                  >
                    🚪 Abmelden
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="lg:pl-64 min-h-screen">
        <div className="container mx-auto px-4 py-6 pb-24 lg:pb-6 max-w-7xl">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      {showNav && (
        <nav
          className={`lg:hidden fixed bottom-0 left-0 right-0 bg-white/80 dark:bg-secondary-800/80 backdrop-blur-lg border-t border-secondary-200 dark:border-secondary-700 z-50 shadow-lg transition-transform duration-300 ${
            showBottomNav ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
        >
          <div className="grid grid-cols-4 h-16">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center transition-all ${
                  isActive(item.path)
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                }`}
              >
                <span className="mb-0.5">{item.icon}</span>
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      )}

      {/* Footer - Desktop Only */}
      <footer className="hidden lg:block lg:ml-64 text-center py-6 px-4 text-sm text-secondary-600 dark:text-secondary-400 border-t border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800">
        <Link
          to="/datenschutz"
          className="text-secondary-600 hover:text-primary-600 transition-colors"
        >
          Datenschutz
        </Link>
        <span className="mx-2">•</span>
        <span>© {new Date().getFullYear()} HEDIT GmbH</span>
      </footer>
    </div>
  );
}

export default Layout;
